/**
 * <predictive-search> — live results for the immersive search dialog.
 *
 * Progressive enhancement over a plain GET form to /search: as the user
 * types (debounced), Shopify's Predictive Search API
 * (`/search/suggest.json?q=…&resources[type]=product`) is queried and
 * product rows (image + title + price) are rendered into [data-results-list].
 *
 * Expected DOM inside the element:
 * - input[type="search"]        the query field (inside the fallback form)
 * - [data-results]              results wrapper, [hidden] until searching
 * - [data-results-list]         <ul> that receives the product rows
 * - [data-status]               aria-live status line (loading / count / empty)
 * - [data-view-all]             "all results" link whose href tracks the query
 *
 * Data attributes (set from Liquid):
 * - data-search-url             routes.predictive_search_url (e.g. /search/suggest)
 * - data-money-format           shop.money_format, HTML stripped
 * - data-text-loading           "Searching…"
 * - data-text-empty             "No products for “[terms]”"
 * - data-text-count-one/-other  "[count] results for “[terms]”"
 */

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 8;
const THUMB_SIZE = 64;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-results]');
    this.list = this.querySelector('[data-results-list]');
    this.status = this.querySelector('[data-status]');
    this.viewAll = this.querySelector('[data-view-all]');
    if (!this.input || !this.results || !this.list) return;

    this.endpoint = `${this.dataset.searchUrl || '/search/suggest'}.json`;
    this.searchUrl = this.viewAll?.getAttribute('href') || '/search';
    this.timer = null;
    this.controller = null;

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), DEBOUNCE_MS);
    });
  }

  get query() {
    return this.input.value.trim();
  }

  search() {
    const query = this.query;
    this.controller?.abort();

    if (query.length < MIN_QUERY_LENGTH) {
      this.reset();
      return;
    }

    this.classList.add('is-searching');
    this.results.hidden = false;
    this.setStatus(this.dataset.textLoading || '…');

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(RESULT_LIMIT),
      'resources[options][unavailable_products]': 'hide',
    });

    this.controller = new AbortController();
    fetch(`${this.endpoint}?${params}`, { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        // Ignore stale responses (query changed while in flight).
        if (this.query !== query) return;
        this.render(data?.resources?.results?.products || [], query);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') this.reset();
      });
  }

  reset() {
    this.classList.remove('is-searching');
    this.results.hidden = true;
    this.list.replaceChildren();
    this.setStatus('');
  }

  render(products, query) {
    this.list.replaceChildren(...products.map((product) => this.buildRow(product)));

    if (this.viewAll) {
      this.viewAll.href = `${this.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
      this.viewAll.hidden = products.length === 0;
    }

    if (products.length === 0) {
      this.setStatus(this.template('textEmpty', products.length, query));
    } else {
      const key = products.length === 1 ? 'textCountOne' : 'textCountOther';
      this.setStatus(this.template(key, products.length, query));
    }
  }

  buildRow(product) {
    const item = document.createElement('li');
    item.className = 'search-dialog__result';

    const link = document.createElement('a');
    link.className = 'search-dialog__result-link';
    link.href = product.url;

    const media = document.createElement('span');
    media.className = 'search-dialog__result-media';
    const imageUrl = product.featured_image?.url || product.image;
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = this.resize(imageUrl, THUMB_SIZE * 2);
      img.alt = '';
      img.width = THUMB_SIZE;
      img.height = THUMB_SIZE;
      img.loading = 'lazy';
      media.appendChild(img);
    }

    const text = document.createElement('span');
    text.className = 'search-dialog__result-text';

    const title = document.createElement('span');
    title.className = 'search-dialog__result-title';
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'search-dialog__result-price';
    price.textContent = this.formatMoney(product.price);

    text.append(title, price);
    link.append(media, text);
    item.appendChild(link);
    return item;
  }

  /** Append a width param to a Shopify CDN image URL. */
  resize(url, width) {
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }

  /** Fill a locale template ("[count] results for “[terms]”"). */
  template(key, count, terms) {
    return (this.dataset[key] || '').replaceAll('[count]', String(count)).replaceAll('[terms]', terms);
  }

  /**
   * Format a decimal price string ("34.99") with the store's money format
   * ("{{ amount_with_comma_separator }}€" etc.).
   */
  formatMoney(price) {
    const value = Number(price);
    if (Number.isNaN(value)) return '';

    const format = this.dataset.moneyFormat || '{{ amount }}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, placeholder) => {
      const noDecimals = placeholder.includes('no_decimals');
      const comma = placeholder.includes('comma_separator');
      const [thousands, decimal] = comma ? ['.', ','] : [',', '.'];
      const fixed = value.toFixed(noDecimals ? 0 : 2);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    });
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
